import Icon from "@react-native-vector-icons/material-design-icons/static";
import { DateTime } from "luxon";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Text, TextInput } from "react-native-paper";
import type { IconType } from "../../icon";
import { useAppTheme } from "../../useAppTheme";
import Box from "../Box";
import CustomDatePickerModal from "./CustomDatePickerModal";

interface CustomDateInputProps {
	value?: string;
	onOk?: (value: Date | { startTime: Date; endTime: Date } | null) => void;
	onChange?: (value: Date | { startTime: Date; endTime: Date } | null) => void;
	label?: string;
	error?: string;
	validRange?: {
		startDate?: Date;
		endDate?: Date;
	};
	showClear?: boolean;
}

const CustomDateInput = ({
	value = "",
	onOk,
	onChange,
	label,
	error,
	validRange,
	showClear = true,
}: CustomDateInputProps) => {
	const theme = useAppTheme();
	const { t } = useTranslation();
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [inputValue, setInputValue] = useState(value);
	const [inputError, setInputError] = useState("");
	const [selectedDate, setSelectedDate] = useState<Date | undefined>();

	useEffect(() => {
		if (value) {
			const date = DateTime.fromFormat(value, "MM/dd/yyyy");
			if (date.isValid) {
				setSelectedDate(date.toJSDate());
			}
		}
	}, [value]);

	const handleOpen = () => {
		setIsModalOpen(true);
	};

	const handleClose = () => {
		setIsModalOpen(false);
	};

	const handleOk = (
		value: Date | { startTime: Date; endTime: Date } | null,
	) => {
		if (value instanceof Date) {
			const formattedDate = DateTime.fromJSDate(value).toFormat("MM/dd/yyyy");
			setInputValue(formattedDate);
			setSelectedDate(value);
			onOk?.(value);
		}
		handleClose();
	};

	const formatDateInput = (value: string): string => {
		const digits = value.replace(/\D/g, "");

		if (digits.length <= 2) {
			return digits;
		}
		if (digits.length <= 4) {
			return `${digits.slice(0, 2)}/${digits.slice(2)}`;
		}
		return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4, 8)}`;
	};

	const handleTextChange = (text: string) => {
		const formattedValue = formatDateInput(text);
		setInputValue(formattedValue);

		if (formattedValue.length === 10) {
			const date = DateTime.fromFormat(formattedValue, "MM/dd/yyyy");
			if (date.isValid) {
				setSelectedDate(date.toJSDate());
				setInputError("");
				onChange?.(date.toJSDate());
			} else {
				setInputError(t("common.datePicker.invalidDateFormatError"));
				setSelectedDate(undefined);
				onChange?.(null);
			}
		} else {
			setInputError("");
			setSelectedDate(undefined);
			onChange?.(null);
		}
	};

	return (
		<>
			<Box position="relative">
				<TextInput
					mode="outlined"
					label={label ?? t("common.datePicker.dateLabel")}
					value={inputValue}
					onChangeText={handleTextChange}
					error={!!error || !!inputError}
					keyboardType="numeric"
					maxLength={10}
					right={
						<TextInput.Icon
							icon={() => (
								<Icon
									name={"calendar-today" as IconType}
									size={24}
									color={theme.colors.onSurfaceVariant}
								/>
							)}
							onPress={handleOpen}
						/>
					}
				/>
				{(error || inputError) && (
					<Box paddingTop={4}>
						<Text variant="bodySmall" style={{ color: theme.colors.error }}>
							{error || inputError}
						</Text>
					</Box>
				)}
			</Box>
			{isModalOpen && (
				<Box>
					<CustomDatePickerModal
						mode="single"
						onClose={handleClose}
						onOk={handleOk}
						date={selectedDate}
						validRange={validRange}
						isClearVisible={showClear}
					/>
				</Box>
			)}
		</>
	);
};

export default CustomDateInput;
